import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { UserRole } from '@/types';
import { useGetMyProductLicensesQuery } from '@/store/api/apiSlice';
import {
  AWAITING_ACCESS_PATH,
  isDeliveryUserRole,
} from '@/utils/productLicense';
import {
  getRequiredProductForPath,
  isProductLicensedPath,
} from '@/utils/productLicensedPaths';
import { shouldUseOrgConsoleNav, isOrgConsolePath } from '@/utils/orgConsole';
import NoProductLicense from './NoProductLicense';

/**
 * Blocks product routes when the signed-in user lacks a seat for that product.
 */
export default function ProductLicenseGuard({ children }: { children: JSX.Element }) {
  const { user } = useAuth();
  const location = useLocation();
  const role = user?.role;

  const skip =
    !user ||
    role === UserRole.SUPER_ADMIN ||
    shouldUseOrgConsoleNav(role, location.pathname) ||
    isOrgConsolePath(location.pathname) ||
    !isProductLicensedPath(location.pathname, location.search);

  const { data, isLoading, isError } = useGetMyProductLicensesQuery(undefined, { skip });

  if (skip) {
    return children;
  }

  if (isLoading) {
    return (
      <div className="min-h-[40vh] flex items-center justify-center bg-gray-50" />
    );
  }

  const productId = getRequiredProductForPath(location.pathname, location.search);
  const launchable = (data?.launchableProductIds ?? []) as string[];

  if (!isError && launchable.length === 0 && isDeliveryUserRole(role)) {
    return <Navigate to={AWAITING_ACCESS_PATH} replace />;
  }

  if (!isError && launchable.includes(productId)) {
    return children;
  }

  return <NoProductLicense productId={productId} />;
}
